// src/pages/message-center/ShiftDetailsPage.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Calendar, CheckCircle, Clock, Edit, Eye, Package, PauseCircle, RotateCcw, Truck, User, UserRound, XCircle, Copy, Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { PageHeader, DataTable, FilterPanel, StatsCards, MultiSelectFilter, ExportProgressModal } from '../../components/common';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { getShiftDetails, getShiftOrders, getShiftTypes } from '../../services/api/messageCenterShifts';
import { getUsers } from '../../services/api/users';
import { getEcomOrder } from '../../services/api/orders';
import { formatDate as formatOrderDate, formatTime, formatDateTime } from '../../utils/dateUtils';
import { exportRowsToExcel } from '../../utils/excelExport';
import { getShiftTypeIcon } from './utils/shiftTypeIcon';
import ShiftModal from './components/ShiftModal';
import OrderDetailsModal from '../orders/components/OrderDetailsModal';
import AirwaybillTrackingModal from '../orders/components/AirwaybillTrackingModal';
import { getAllOrderReferenceData } from '../../services/api/orderReference';
import styles from './ShiftDetailsPage.module.css';

const ORDER_GROUP_KEYS = ['totalOrders', 'dispatchedOrders', 'settledOrders'];
const MAX_EXPORT_RECORDS = 150000;
const EXPORT_BATCH_SIZE  = 500;
const EMPTY_ORDER_FILTERS = { statusKeys: [], courierStatusLabels: [] };
const getItemsQty = (orderItems = []) => orderItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
const formatOrderItems = (orderItems = []) => orderItems.map((item) => `${item.Product?.name || 'N/A'} × ${item.quantity}`).join(', ');
const STATUS_CARD_ICONS = {
  pending: Clock,
  confirmed: CheckCircle,
  not_confirmed: XCircle,
  cancelled: XCircle,
  hold: PauseCircle,
  in_packing: Package,
  in_courier: Truck,
  settled: CheckCircle,
  returned: RotateCcw,
};


const ShiftDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, formatCurrency, formatDate } = useLocalization();
  const { error: showError } = useAlert();
  const { hasPermission } = usePermissions();

  const [shift, setShift] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [shiftTypes, setShiftTypes] = useState([]);
  const [businessUsers, setBusinessUsers] = useState([]);
  const [showEditModal, setShowEditModal] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);
  const [isOrderLoading, setIsOrderLoading] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  const [trackingOrder, setTrackingOrder] = useState(null);

  const handleCopyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => setCopiedStates(prev => ({ ...prev, [key]: false })), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      showError('Failed to copy to clipboard');
    }
  };

  const getOrderStatusIcon = (statusKey) => {
    const icons = {
      pending:       <Clock size={14} />,
      confirmed:     <CheckCircle size={14} />,
      hold:          <Clock size={14} />,
      not_confirmed: <XCircle size={14} />,
      cancelled:     <XCircle size={14} />,
      in_courier:    <Truck size={14} />,
      settled:       <CheckCircle size={14} />,
      returned:      <XCircle size={14} />
    };
    return icons[statusKey] || <Clock size={14} />;
  };

  const handleViewOrder = async (row) => {
    setIsOrderLoading(true);
    try {
      const { order } = await getEcomOrder(row.id);
      setSelectedOrder(order);
      setShowOrderModal(true);
    } catch (err) {
      console.error('Error loading order:', err);
      showError(err.response?.data?.message || t('ecomOrders.failedToLoadOrder'));
    } finally {
      setIsOrderLoading(false);
    }
  };

  const loadShift = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await getShiftDetails(id);
      setShift(data.shift);
    } catch (err) {
      console.error('Error loading shift details:', err);
      showError(err.response?.data?.message || t('messageCenterShifts.failedToLoadShiftDetails'));
    } finally {
      setIsLoading(false);
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadShift();
  }, [loadShift]);

  useEffect(() => {
    (async () => {
      try {
        const data = await getShiftTypes();
        setShiftTypes(data.shiftTypes || []);
      } catch (err) {
        console.error('Error loading shift types:', err);
      }
    })();
    (async () => {
      try {
        const data = await getUsers({ status: 'true', page: 1, limit: 200 });
        setBusinessUsers(data.users || []);
      } catch (err) {
        console.error('Error loading business users:', err);
      }
    })();
  }, []);

  // ── Orders sub-list: its own paginated/sortable/searchable fetch cycle ──────
  const [orders, setOrders] = useState([]);
  const [isOrdersLoading, setIsOrdersLoading] = useState(true);
  const [ordersPagination, setOrdersPagination] = useState({
    currentPage: 1,
    pageSize: 15,
    total: 0,
    pageSizeOptions: [10, 15, 25, 50],
  });
  const [ordersSorting, setOrdersSorting] = useState({ orderBy: 'createdAt', orderDirection: 'DESC' });
  const [ordersSearch, setOrdersSearch] = useState('');
  const [showOrdersFilters, setShowOrdersFilters] = useState(false);
  const [ordersFilters, setOrdersFilters] = useState(EMPTY_ORDER_FILTERS);
  const [orderStatusCounts, setOrderStatusCounts] = useState([]);
  const [courierStatusCounts, setCourierStatusCounts] = useState([]);
  const [orderStatuses, setOrderStatuses] = useState([]);

  useEffect(() => {
    getAllOrderReferenceData()
      .then((data) => setOrderStatuses(data.orderStatuses || []))
      .catch(() => setOrderStatuses([]));
  }, []);

  const buildOrdersParams = () => {
    const params = {
      orderBy: ordersSorting.orderBy,
      orderDirection: ordersSorting.orderDirection,
    };
    if (Array.isArray(ordersSearch)) {
      if (ordersSearch.length > 0) params.multiSearch = ordersSearch.join(',');
    } else if (ordersSearch) {
      params.search = ordersSearch;
    }
    if (ordersFilters.statusKeys.length > 0) params.statusKeys = ordersFilters.statusKeys.join(',');
    if (ordersFilters.courierStatusLabels.length > 0) params.courierStatusLabels = ordersFilters.courierStatusLabels.join(',');
    return params;
  };

  const loadOrders = useCallback(async () => {
    try {
      setIsOrdersLoading(true);
      const params = {
        page: ordersPagination.currentPage,
        pageSize: ordersPagination.pageSize,
        ...buildOrdersParams(),
      };

      const data = await getShiftOrders(id, params);
      setOrders(data.orders || []);
      setOrderStatusCounts(data.orderStatusCounts || []);
      setCourierStatusCounts(data.courierStatusCounts || []);
      setOrdersPagination((prev) => ({ ...prev, total: data.total || 0 }));
    } catch (err) {
      console.error('Error loading shift orders:', err);
      showError(err.response?.data?.message || t('messageCenterShifts.failedToLoadShiftOrders'));
    } finally {
      setIsOrdersLoading(false);
    }
  }, [
    id,
    ordersPagination.currentPage,
    ordersPagination.pageSize,
    ordersSorting.orderBy,
    ordersSorting.orderDirection,
    ordersSearch,
    ordersFilters,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    loadOrders();
  }, [loadOrders]);

  const handleOrdersSearchChange = (value) => {
    setOrdersSearch(value);
    setOrdersPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleOrdersFilterChange = (newFilters) => {
    setOrdersFilters({ ...EMPTY_ORDER_FILTERS, ...newFilters });
    setOrdersPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const toggleStatusFilter = (statusKey) => {
    setOrdersFilters((prev) => ({
      ...prev,
      statusKeys: prev.statusKeys.includes(statusKey)
        ? prev.statusKeys.filter((k) => k !== statusKey)
        : [...prev.statusKeys, statusKey],
    }));
    setOrdersPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const statusCardsData = orderStatusCounts
    .filter((s) => s.count > 0)
    .map((s) => ({
      key: s.key,
      label: s.label,
      value: s.count,
      icon: STATUS_CARD_ICONS[s.key] || Clock,
      color: s.colorCode,
      bgColor: s.colorCode ? `${s.colorCode}15` : undefined,
      active: ordersFilters.statusKeys.includes(s.key),
      onClick: () => toggleStatusFilter(s.key),
    }));

  const ordersFilterDefinitions = [
    {
      key: 'statusKeys',
      label: t('messageCenterShifts.orderStatusFilter'),
      type: 'custom',
      render: (value, onChange) => (
        <MultiSelectFilter
          options={orderStatuses.map((s) => ({ key: s.key, label: s.label }))}
          selectedValues={value}
          onChange={onChange}
          placeholder={t('messageCenterShifts.allStatuses')}
          searchPlaceholder={t('messageCenterShifts.searchStatuses')}
          valueKey="key"
          displayKey="label"
          searchKey="label"
        />
      ),
    },
    {
      key: 'courierStatusLabels',
      label: t('messageCenterShifts.courierStatusFilter'),
      type: 'custom',
      render: (value, onChange) => (
        <MultiSelectFilter
          options={courierStatusCounts.map((s) => ({ key: s.label, label: s.label }))}
          selectedValues={value}
          onChange={onChange}
          placeholder={t('messageCenterShifts.allStatuses')}
          searchPlaceholder={t('messageCenterShifts.searchStatuses')}
          valueKey="key"
          displayKey="label"
          searchKey="label"
        />
      ),
    },
  ];

  const handleOrdersPageSizeChange = (newSize) => {
    setOrdersPagination((prev) => ({ ...prev, pageSize: newSize, currentPage: 1 }));
  };

  // ── Export ──────────────────────────────────────────────────────────────────
  const [exportModalOpen, setExportModalOpen]           = useState(false);
  const [exportStatus, setExportStatus]                 = useState('idle');
  const [exportError, setExportError]                   = useState('');
  const [exportTotalRecords, setExportTotalRecords]     = useState(0);
  const [exportFetchedRecords, setExportFetchedRecords] = useState(0);
  const exportAbortRef = useRef(false);

  const handleExport = async () => {
    setExportModalOpen(true);
    setExportStatus('running');
    setExportError('');
    setExportTotalRecords(0);
    setExportFetchedRecords(0);
    exportAbortRef.current = false;

    try {
      const filterParams = buildOrdersParams();
      const firstPage    = await getShiftOrders(id, { ...filterParams, page: 1, pageSize: 1 });
      const total        = Math.min(firstPage.total || 0, MAX_EXPORT_RECORDS);

      if (total === 0) {
        setExportStatus('error');
        setExportError('No orders to export with the current filters.');
        return;
      }

      setExportTotalRecords(total);
      const allOrders  = [];
      const totalPages = Math.ceil(total / EXPORT_BATCH_SIZE);

      for (let page = 1; page <= totalPages; page++) {
        if (exportAbortRef.current) return;
        const data = await getShiftOrders(id, { ...filterParams, page, pageSize: EXPORT_BATCH_SIZE });
        allOrders.push(...(data.orders || []));
        setExportFetchedRecords(Math.min(allOrders.length, total));
      }
      if (exportAbortRef.current) return;

      await exportRowsToExcel({
        sheetName: 'Shift Orders',
        filename: `mc-shift-${shift?.shiftDate || id}-orders-${new Date().toISOString().split('T')[0]}`,
        columns: [
          { header: t('ecomOrders.orderNumber'), key: 'orderNumber', width: 20 },
          { header: t('ecomOrders.airwaybillNumber'), key: 'airwaybillNumber', width: 20 },
          { header: t('messageCenterShifts.orderItems'), key: 'orderItems', width: 40 },
          { header: t('messageCenterShifts.itemsQty'), key: 'itemsQty', width: 12 },
          { header: t('ecomOrders.total'), key: 'total', width: 16, numFmt: '#,##0.00' },
          { header: t('common.status'), key: 'status', width: 18 },
          { header: t('messageCenterShifts.courierStatus'), key: 'courierStatus', width: 22 },
          { header: t('ecomOrders.createdBy'), key: 'createdBy', width: 22 },
          { header: t('ecomOrders.createdAt'), key: 'createdAt', width: 22 },
        ],
        rows: allOrders.map((row) => ({
          orderNumber: row.orderNumber || '',
          airwaybillNumber: row.airwaybillNumber || '',
          orderItems: formatOrderItems(row.orderItems),
          itemsQty: getItemsQty(row.orderItems),
          total: parseFloat(row.eligibleOrderAmount) || 0,
          status: row.OrderStatus?.label || '',
          courierStatus: row.courierStatus?.statusLabel || '',
          createdBy: row.createdBy?.name || '',
          createdAt: row.createdAt ? formatDateTime(row.createdAt) : '',
        })),
      });

      setExportStatus('done');
    } catch (err) {
      console.error('Export error:', err);
      setExportStatus('error');
      setExportError('Failed to export orders. Please try again.');
    }
  };

  const handleCloseExportModal = () => {
    exportAbortRef.current = true;
    setExportModalOpen(false);
    setExportStatus('idle');
  };

  const target = parseFloat(shift?.targetSalesValue) || 0;
  const achieved = parseFloat(shift?.achievedSalesValue) || 0;
  const isAchieved = shift?.achievedSalesValue != null && achieved >= target;

  const withEstimate = (content) => (
    shift?.isEstimate ? (
      <span className={styles.estimateValue}>
        {content}
        <span className={styles.estimateBadge}>{t('messageCenterShifts.estimate')}</span>
      </span>
    ) : content
  );

  const staffColumns = [
    { key: 'userId', header: t('messageCenterCommission.userId'), width: '80px', render: (row) => row.userId },
    { key: 'user', header: t('messageCenterCommission.staffMember'), width: '260px', render: (row) => row.user?.name || '—' },
    { key: 'startTime', header: t('messageCenterShifts.startTime'), width: '100px', render: (row) => row.startTime },
    { key: 'endTime', header: t('messageCenterShifts.endTime'), width: '100px', render: (row) => row.endTime },
    { key: 'hours', header: t('messageCenterShifts.hours'), width: '80px', render: (row) => Number(row.hours || 0).toFixed(2) },
    {
      key: 'commissionAmount',
      header: t('messageCenterCommission.commissionAmount'),
      width: '240px',
      render: (row) => withEstimate(formatCurrency(row.commissionAmount)),
    },
  ];

  const orderColumns = [
    {
      key: 'orderNumber',
      header: t('ecomOrders.orderNumber'),
      sortable: true,
      width: '140px',
      render: (row) => {
        const copyKey  = `order-${row.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.orderNumberCell}>
            <div className={styles.orderNumber} onClick={(e) => { e.stopPropagation(); handleViewOrder(row); }}>
              #{row.orderNumber}
            </div>
            <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(row.orderNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy order number'}>
              {isCopied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        );
      },
    },
    {
      key: 'airwaybillNumber',
      header: t('ecomOrders.airwaybillNumber'),
      width: '160px',
      render: (row) => {
        if (!row.airwaybillNumber) return <span className={styles.naText}>{t('common.na')}</span>;
        const copyKey  = `awb-${row.id}`;
        const isCopied = copiedStates[copyKey];

        return (
          <div className={styles.courierInfo}>
            <div className={styles.airwaybillWrapper}>
              <button className={styles.airwaybillNumber} onClick={(e) => { e.stopPropagation(); setTrackingOrder(row); }} title={t('ecomOrders.viewPackageTimeline')}>
                {row.airwaybillNumber}
              </button>
              <button className={styles.copyButton} onClick={(e) => { e.stopPropagation(); handleCopyToClipboard(row.airwaybillNumber, copyKey); }} title={isCopied ? 'Copied!' : 'Copy airwaybill number'}>
                {isCopied ? <Check size={12} /> : <Copy size={12} />}
              </button>
            </div>
          </div>
        );
      },
    },
    {
      key: 'orderItems',
      header: t('messageCenterShifts.orderItems'),
      render: (row) => (
        <div className={styles.productsInfo}>
          {row.orderItems?.slice(0, 3).map((item, idx) => (
            <div key={idx} className={styles.productItem}>{item.Product?.name || 'N/A'} × {item.quantity}</div>
          ))}
          {row.orderItems?.length > 3 && <div className={styles.productItem}>+{row.orderItems.length - 3} more</div>}
        </div>
      ),
    },
    {
      key: 'itemsQty',
      header: t('messageCenterShifts.itemsQty'),
      width: '100px',
      render: (row) => <span className={styles.itemsQty}>{getItemsQty(row.orderItems)}</span>,
    },
    {
      key: 'total',
      header: t('ecomOrders.total'),
      sortable: true,
      width: '140px',
      render: (row) => <span className={styles.totalAmount}>{formatCurrency(row.eligibleOrderAmount)}</span>,
    },
    {
      key: 'status',
      header: t('common.status'),
      width: '180px',
      render: (row) => (
        <div className={styles.statusCell}>
          <div
            className={styles.statusBadge}
            style={{
              backgroundColor: row.OrderStatus?.colorCode ? `${row.OrderStatus.colorCode}20` : '#F59E0B20',
              borderColor: row.OrderStatus?.colorCode || '#F59E0B',
              color: row.OrderStatus?.colorCode || '#92400e',
            }}
          >
            {getOrderStatusIcon(row.OrderStatus?.key)}
            <span>{row.OrderStatus?.label || '—'}</span>
          </div>
        </div>
      ),
    },
    {
      key: 'courierStatus',
      header: t('messageCenterShifts.courierStatus'),
      sortable: true,
      width: '180px',
      render: (row) => {
        const label = row.courierStatus?.statusLabel;
        if (!label) return <span className={styles.naText}>{t('common.na')}</span>;
        const colorCode = row.courierStatus?.colorCode;
        return (
          <div
            className={styles.courierStatusBadge}
            title={label}
            style={colorCode ? { backgroundColor: `${colorCode}20`, borderColor: colorCode, color: colorCode } : undefined}
          >
            <span className={styles.courierStatusText}>{label}</span>
          </div>
        );
      },
    },
    {
      key: 'createdBy',
      header: t('ecomOrders.createdBy'),
      width: '160px',
      render: (row) => (
        <div className={styles.createdByInfo}><User size={12} />{row.createdBy?.name || <span className={styles.naText}>—</span>}</div>
      ),
    },
    {
      key: 'createdAt',
      header: t('ecomOrders.createdAt'),
      sortable: true,
      width: '160px',
      render: (row) => (
        <div className={styles.dateCell}>
          <div className={styles.dateInfo}><Calendar size={12} />{formatOrderDate(row.createdAt)}</div>
          <div className={styles.timeInfo}>{formatTime(row.createdAt)}</div>
        </div>
      ),
    },
  ];

  const orderActions = {
    render: (row) => (
      <div className={styles.actionButtons}>
        {hasPermission('view_ecom_order') && (
          <Link
            to={`/orders/${row.id}`}
            className={styles.actionIconButton}
            title={t('common.view')}
            onClick={(e) => e.stopPropagation()}
          >
            <Eye size={14} />
          </Link>
        )}
        {hasPermission('edit_ecom_order') && (
          <Link
            to={`/orders/${row.id}/edit`}
            className={styles.actionIconButton}
            title={t('common.edit')}
            onClick={(e) => e.stopPropagation()}
          >
            <Edit size={14} />
          </Link>
        )}
      </div>
    ),
    width: '90px',
    frozen: true,
  };

  const slabColumns = [
    { key: 'minValue', header: t('messageCenterShiftTypes.minValue'), render: (row) => row.minValue },
    {
      key: 'maxValue',
      header: t('messageCenterShiftTypes.maxValue'),
      render: (row) => (row.maxValue != null ? row.maxValue : t('messageCenterShiftTypes.openEnded')),
    },
    { key: 'rate', header: t('messageCenterShifts.amountPerUnit'), render: (row) => `${row.rate}%` },
    { key: 'slabQty', header: t('messageCenterShifts.slabQty'), render: (row) => row.slabQty },
    { key: 'slabTotal', header: t('messageCenterShifts.slabTotal'), render: (row) => withEstimate(formatCurrency(row.slabTotal)) },
  ];

  return (
    <div className={styles.shiftDetailsPage}>
      <PageHeader
        title={shift ? `${t('messageCenterShifts.detailsPageTitle')} — ${formatDate(shift.shiftDate)}` : t('messageCenterShifts.detailsPageTitle')}
        description={t('messageCenterShifts.detailsPageDescription')}
        icon={Clock}
        actions={
          <div className={styles.pageActions}>
            {hasPermission('manage_message_center_shifts') && shift && (
              <button onClick={() => setShowEditModal(true)} className={styles.editButton}>
                <Edit size={18} />
                {t('messageCenterShifts.edit')}
              </button>
            )}
            <button onClick={() => navigate('/message-center/shifts')} className={styles.backButton}>
              <ArrowLeft size={18} />
              {t('common.back')}
            </button>
          </div>
        }
      />

      {shift && (
        <>
          <div className={styles.identityCards}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('messageCenterShifts.shiftLeader')}</span>
              <span className={styles.summaryValue}>
                <UserRound size={16} className={styles.shiftTypeIcon} />
                {shift.leader?.name || '—'}
              </span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('messageCenterShifts.shiftType')}</span>
              <span className={styles.summaryValue}>
                {(() => {
                  const ShiftTypeIcon = getShiftTypeIcon(shift.shiftType?.name);
                  return <ShiftTypeIcon size={16} className={styles.shiftTypeIcon} />;
                })()}
                {shift.shiftType?.name || '—'}
              </span>
            </div>
          </div>

          {(shift.excludeAllFromCommission || shift.excludeLeaderFromCommission) && (
            <div className={styles.infoBannerGrid}>
              {shift.excludeAllFromCommission && (
                <div className={styles.infoBanner}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <p>{t('messageCenterShifts.excludeAllFromCommission')}</p>
                </div>
              )}
              {shift.excludeLeaderFromCommission && (
                <div className={styles.infoBanner}>
                  <AlertTriangle size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                  <p>{t('messageCenterShifts.excludeLeaderFromCommission')}</p>
                </div>
              )}
            </div>
          )}

          <div className={styles.summaryCards}>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('messageCenterShifts.targetSalesValue')}</span>
              <span className={styles.summaryValue}>{formatCurrency(target)}</span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('messageCenterShifts.achievedSalesValue')}</span>
              <span className={`${styles.summaryValue} ${isAchieved ? styles.achieved : styles.notAchieved}`}>
                {withEstimate(formatCurrency(achieved))}
              </span>
            </div>
            <div className={styles.summaryCard}>
              <span className={styles.summaryLabel}>{t('messageCenterCommission.totalCommission')}</span>
              <span className={styles.summaryValue}>{withEstimate(formatCurrency(shift.totalCommission))}</span>
            </div>
          </div>

          <div className={styles.breakdownSection}>
            {ORDER_GROUP_KEYS.map((key) => {
              const group = shift.orderGroups?.[key] || { salesValue: 0, orderQuantity: 0, itemQuantity: 0 };
              return (
                <div className={styles.breakdownCard} key={key}>
                  <h3>{t(`messageCenterShifts.${key}`)}</h3>
                  <div className={styles.breakdownList}>
                    <div className={styles.breakdownItem}>
                      <span className={styles.breakdownLabel}>{t('messageCenterShifts.salesValue')}</span>
                      <span className={styles.breakdownValue}>{formatCurrency(group.salesValue)}</span>
                    </div>
                    <div className={styles.breakdownItem}>
                      <span className={styles.breakdownLabel}>{t('messageCenterShifts.orderQuantity')}</span>
                      <span className={styles.breakdownValue}>{group.orderQuantity}</span>
                    </div>
                    <div className={styles.breakdownItem}>
                      <span className={styles.breakdownLabel}>{t('messageCenterShifts.itemQuantity')}</span>
                      <span className={styles.breakdownValue}>{group.itemQuantity}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <h3 className={styles.sectionTitle}>{t('messageCenterShifts.commissionCalculation')}</h3>
          <DataTable
            data={shift.slabBreakdown || []}
            columns={slabColumns}
            emptyMessage={t('messageCenterShiftTypes.noSlabsConfigured')}
          />

          <h3 className={styles.sectionTitle}>{t('messageCenterShifts.staffCommissionBreakdown')}</h3>
          <DataTable
            data={shift.staffBreakdown || []}
            columns={staffColumns}
            loading={isLoading}
            emptyMessage={t('messageCenterCommission.noStaffCommissionRecords')}
          />

          <h3 className={styles.sectionTitle}>{t('messageCenterShifts.shiftOrders')}</h3>
          {statusCardsData.length > 0 && (
            <div className={styles.statusCards}>
              <StatsCards
                stats={statusCardsData}
                clickable
                columns={Math.min(statusCardsData.length, 5)}
                minColumnWidth="140px"
                gap="0.75rem"
                valueFontSize="1.25rem"
              />
            </div>
          )}
          <FilterPanel
            filters={ordersFilterDefinitions}
            values={ordersFilters}
            onChange={handleOrdersFilterChange}
            onClear={() => {
              setOrdersSearch('');
              setOrdersFilters(EMPTY_ORDER_FILTERS);
              setOrdersPagination((prev) => ({ ...prev, currentPage: 1 }));
            }}
            show={showOrdersFilters}
            onToggle={() => setShowOrdersFilters(!showOrdersFilters)}
            onExport={handleExport}
            showExport={true}
            exportButtonText={t('common.export')}
            searchConfig={{
              show: true,
              value: ordersSearch,
              onChange: handleOrdersSearchChange,
              placeholder: t('messageCenterShifts.searchShiftOrders'),
              multiSearch: true,
            }}
            pageSizeConfig={{
              show: true,
              value: ordersPagination.pageSize,
              onChange: handleOrdersPageSizeChange,
              options: ordersPagination.pageSizeOptions,
            }}
          />
          <DataTable
            data={orders}
            columns={orderColumns}
            actions={orderActions}
            sorting={ordersSorting}
            onSortChange={setOrdersSorting}
            pagination={ordersPagination}
            onPaginationChange={setOrdersPagination}
            loading={isOrdersLoading || isOrderLoading}
            onRowClick={handleViewOrder}
            emptyMessage={t('messageCenterShifts.noOrdersForShift')}
          />
        </>
      )}

      {showEditModal && shift && (
        <ShiftModal
          mode="edit"
          shift={shift}
          shiftTypes={shiftTypes}
          businessUsers={businessUsers}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            setShowEditModal(false);
            loadShift();
          }}
        />
      )}

      {showOrderModal && selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          onClose={() => { setShowOrderModal(false); setSelectedOrder(null); }}
          onUpdate={(updated) => setSelectedOrder(updated)}
        />
      )}

      {trackingOrder && (
        <AirwaybillTrackingModal
          orderId={trackingOrder.id}
          airwaybillNumber={trackingOrder.airwaybillNumber}
          onClose={() => setTrackingOrder(null)}
        />
      )}

      <ExportProgressModal
        isOpen={exportModalOpen}
        onClose={handleCloseExportModal}
        status={exportStatus}
        error={exportError}
        totalRecords={exportTotalRecords}
        fetchedRecords={exportFetchedRecords}
      />
    </div>
  );
};

export default ShiftDetailsPage;
